import axios from "axios";
import { useEffect, useState, useRef } from "react";
import ApiUrl from "../Environment/ApiUrl";
import { useNavigate } from "react-router-dom";
import { useToast } from "../Toast/ToastContext";
import "../../assets/css/Loader.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

function Product() {
  const noProductsShown = useRef(false); // keeps track of whether toast already shown

  const [showModal, setShowModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteShowModals, setDeleteShowModals] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const toast = useToast();
  const navigate = useNavigate();

  // Notification state
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Product state
  const [responseData, setResponseData] = useState([]);
  const [generateNumber, setGenerateNumber] = useState("");
  const [typeGetting, setTypeGetting] = useState([]);
  const [updateId, setUpdateId] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [deleteName, setDeleteName] = useState("");

  const [isTranslating, setIsTranslating] = useState(false);

  // Auto-translate English item name to Tamil using MyMemory free API
// Better translation using multiple free APIs as fallbacks
const translateToTamil = async (englishText) => {
  if (!englishText || englishText.trim().length < 2) return "";
  
  setIsTranslating(true);
  
  // Try multiple free translation APIs
  const apis = [
    // MyMemory API (free, no key needed)
    async () => {
      const encoded = encodeURIComponent(englishText.trim());
      const res = await fetch(`https://api.mymemory.translated.net/get?q=${encoded}&langpair=en|ta`);
      const data = await res.json();
      const translated = data?.responseData?.translatedText;
      // Check if translation worked (not just returning the same English text)
      if (translated && translated.toLowerCase() !== englishText.toLowerCase()) {
        return translated;
      }
      return null;
    },
    
    // Lingva Translate (free, no key, uses Google Translate)
    async () => {
      const encoded = encodeURIComponent(englishText.trim());
      const res = await fetch(`https://lingva.ml/api/v1/en/ta/${encoded}`);
      if (res.ok) {
        const data = await res.json();
        if (data.translation && data.translation !== englishText) {
          return data.translation;
        }
      }
      return null;
    },
    
    // Alternative: LibreTranslate (free instance)
    async () => {
      const res = await fetch('https://libretranslate.com/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: englishText.trim(),
          source: 'en',
          target: 'ta',
          format: 'text'
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.translatedText) {
          return data.translatedText;
        }
      }
      return null;
    }
  ];
  
  // Try APIs in sequence until one works
  for (const api of apis) {
    try {
      const result = await api();
      if (result) {
        setIsTranslating(false);
        return result;
      }
    } catch (error) {
      console.warn("Translation API failed:", error);
      continue;
    }
  }
  
  setIsTranslating(false);
  return ""; // Return empty if all fail
};

  const [addData, setAddData] = useState({
    item_code: "",
    item_name: "",
    item_name_english: "",
    item_name_tamil: "",
    category: "",
    // type: "",  // commented out - using item_name_english/tamil instead
    type: "",
    status: "",
    pure_percent: "", // THIS MUST BE HERE
    image: null,
    created_by: "system",
    created_date: "",
    updated_by: "",
    updated_date: "",
  });

  const [updateData, setUpdateData] = useState({
    id: "",
    item_code: "",
    item_name: "",
    item_name_english: "",
    item_name_tamil: "",
    category: "",
    // type: "",  // commented out
    type: "",
    status: "",
    pure_percent: "", // THIS MUST BE HERE
    image: null,
    created_by: "",
    created_date: "",
    updated_by: "system",
    updated_date: "",
    remove_image: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal functions for notification
  const openNotificationModal = (id, action) => {
    setSelectedProductId(id);
    setActionType(action);
    setShowModal(true);
  };

  const closeNotificationModal = () => {
    setShowModal(false);
    setSelectedProductId(null);
    setActionType("");
    setIsSubmitting(false);
  };

  // Fetch notification data
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/notification.php`);
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
      toast.error("Failed to fetch notifications", 3000, "topRight");
    }
  };

  // Check notification status
  const checkNotificationStatus = (productId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.find(
      (notif) =>
        notif.table_id === productId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "accepted" &&
        notif.employe_id === userId
    );
  };

  // Check for existing pending notification
  const hasPendingNotification = (productId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.find(
      (notif) =>
        notif.table_id === productId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "requested" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  // Handle notification submission
  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;
    setIsSubmitting(true);

    if (hasPendingNotification(selectedProductId, actionType)) {
      toast.warning("A pending request for this action already exists!");
      closeNotificationModal();
      return;
    }

    try {
      const userId = localStorage.getItem("username");
      const currentTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      const formData = {
        table_name: "product",
        table_id: selectedProductId,
        employe_id: userId || "",
        status: "requested",
        button_value: actionType,
        time: currentTime,
        viewed: "false",
        created_date: currentTime,
        updated_by: userId || "",
        updated_date: currentTime,
        pure_percent: "",
      };

      await axios.post(`${ApiUrl}/Api/notification.php`, formData, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(`${actionType} request sent successfully!`);
      closeNotificationModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error submitting notification:", error.message);
      toast.error("Error submitting notification. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch product data
  const fetchData = async () => {
    try {
      const localRole = localStorage.getItem("role");
      const localUsername = localStorage.getItem("username");

      const response = await axios.get(
        localRole === "Employee"
          ? `${ApiUrl}/Api/product.php?employee_id=${localUsername}`
          : `${ApiUrl}/Api/product.php`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status) {
        setResponseData(response.data?.data || []);
        noProductsShown.current = false; // reset flag if products come later
      } else {
        console.error("API error:", response.data.message);

        if (!noProductsShown.current) {
          toast.error(
            response.data.message || "No products found",
            3000,
            "topRight"
          );
          noProductsShown.current = true; // mark as shown
        }
      }
    } catch (error) {
      console.error("Fetch data error:", error.message);

      if (!noProductsShown.current) {
        toast.error("Failed to fetch data", 3000, "topRight");
        noProductsShown.current = true;
      }
    }
  };

  const fetchProductType = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/product_type.php`);
      setTypeGetting(response.data?.data || []);
    } catch (error) {
      toast.error("Failed to fetch product types", 3000, "topRight");
    }
  };

  const fetchDataGenerate = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/product.php/generate-item-code`
      );
      setGenerateNumber(response.data?.item_code || "");
      setAddData((prev) => ({
        ...prev,
        item_code: response.data?.item_code || "",
      }));
      openModal();
    } catch (error) {
      toast.error("Failed to fetch generate code", 3000, "topRight");
    }
  };

  const handleImageChange = (e) => {
    setAddData({ ...addData, image: e.target.files[0] });
  };

  const handleUpdateImageChange = (e) => {
    setUpdateData({
      ...updateData,
      image: e.target.files[0],
      remove_image: false,
    });
  };

  const handleRemoveUpdateImage = () => {
    setUpdateData((prev) => ({
      ...prev,
      image: null,
      remove_image: true,
    }));
    const input = document.getElementById("update_image");
    if (input) input.value = "";
  };
  // FIX FOR ADD PRODUCT
  // ----- ADD SUBMIT -----
const handleSubmit = async (e) => {
  e.preventDefault();

  const errors = {};
  if (!addData.item_name) errors.item_name = "Item name is required";
  if (!addData.category) errors.category = "Category is required";
  if (!addData.status) errors.status = "Status is required";
  if (!addData.pure_percent && addData.pure_percent == 0) {
    errors.pure_percent = "Purity percentage is required";
  } else if (isNaN(addData.pure_percent) || addData.pure_percent < 0 || addData.pure_percent > 100) {
    errors.pure_percent = "Purity must be a number between 0 and 100";
  }

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  try {
    setLoading(true);
    const formData = new FormData();

    // Auto-translate if Tamil name is empty
    let finalTamilName = addData.item_name_tamil;
    if (!finalTamilName && addData.item_name_english) {
      finalTamilName = await translateToTamil(addData.item_name_english);
    }

    formData.append("item_code", addData.item_code || "");
    formData.append("item_name", addData.item_name_english || addData.item_name || "");
    formData.append("item_name_english", addData.item_name_english || "");
    formData.append("item_name_tamil", finalTamilName || ""); // Send translated Tamil
    formData.append("category", addData.category || "");
    formData.append("type", addData.type || "");
    formData.append("status", addData.status || "");
    formData.append("pure_percent", addData.pure_percent || "");
    formData.append("created_by", "system");

    if (addData.image) {
      formData.append("image", addData.image);
    }

    const response = await axios.post(`${ApiUrl}/Api/product.php`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    toast.success("Product added successfully!");
    closeModal();
    fetchData();
  } catch (error) {
    console.error("Add error:", error);
    toast.error(error.response?.data?.message || "Failed to add product");
  } finally {
    setLoading(false);
  }
};
const handleUpdateSubmit = async (e) => {
  e.preventDefault();

  const errors = {};
  if (!updateData.item_name) errors.item_name = "Item name is required";
  if (!updateData.category) errors.category = "Category is required";
  if (!updateData.status) errors.status = "Status is required";

  if (updateData.pure_percent === undefined || updateData.pure_percent === null || updateData.pure_percent === '') {
    errors.pure_percent = "Purity percentage is required";
  } else if (isNaN(updateData.pure_percent)) {
    errors.pure_percent = "Purity must be a number";
  } else {
    const num = parseFloat(updateData.pure_percent);
    if (num < 0 || num > 100) {
      errors.pure_percent = "Purity must be between 0 and 100";
    }
  }

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  try {
    setLoading(true);
    const formData = new FormData();

    // Auto-translate if Tamil name is empty and English name changed
    let finalTamilName = updateData.item_name_tamil;
    const englishName = updateData.item_name_english || updateData.item_name;
    if (!finalTamilName && englishName) {
      finalTamilName = await translateToTamil(englishName);
    }

    formData.append("id", String(updateData.id));
    formData.append("item_code", updateData.item_code || "");
    formData.append("item_name", englishName || "");
    formData.append("item_name_english", englishName || "");
    formData.append("item_name_tamil", finalTamilName || "");
    formData.append("category", updateData.category || "");
    formData.append("type", updateData.type || "");
    formData.append("pure_percent", updateData.pure_percent || "");
    formData.append("status", updateData.status || "");
    formData.append("updated_by", "system");
    formData.append("remove_image", updateData.remove_image ? "true" : "false");

    if (updateData.image instanceof File) {
      formData.append("image", updateData.image);
    }

    const response = await axios.put(`${ApiUrl}/Api/product.php`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    toast.success("Product updated successfully!");
    closeUpdateModal();
    fetchData();
    fetchNotifications();
  } catch (error) {
    console.error("Update error:", error);
    toast.error(error.response?.data?.message || "Failed to update product");
  } finally {
    setLoading(false);
  }
};

  const handleDelete = async () => {
    try {
      setLoading(true);

      await axios.delete(`${ApiUrl}/Api/product.php/${deleteId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Product deleted successfully!", 3000, "topRight");
      handleCloseDeleteModal();
      fetchData();
      fetchNotifications();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete product",
        3000,
        "topRight"
      );
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => {
    setShowModal(false);
    setAddData({
      item_code: "",
      item_name: "",
      category: "",
      type: "",
      status: "",
      pure_percent: "", // Make sure this is reset
      image: null,
      created_by: "system",
      created_date: "",
      updated_by: "",
      updated_date: "",
    });
    setGenerateNumber("");
    setFormErrors({});
  };

  const closeUpdateModal = () => {
    setUpdateModal(false);
    setUpdateData({
      id: "",
      item_code: "",
      item_name: "",
      category: "",
      type: "",
      status: "",
      pure_percent: "", // Make sure this is reset
      image: null,
      created_by: "",
      created_date: "",
      updated_by: "system",
      updated_date: "",
      remove_image: false,
    });
    setFormErrors({});
  };

  const handleOpenDeleteModal = (id, item_name) => {
    const userRole = localStorage.getItem("role");

    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Delete")) {
        setDeleteId(id);
        setDeleteName(item_name);
        setDeleteShowModals(true);
      } else {
        openNotificationModal(id, "Delete");
      }
    } else {
      setDeleteId(id);
      setDeleteName(item_name);
      setDeleteShowModals(true);
    }
  };

  const handleCloseDeleteModal = () => setDeleteShowModals(false);

  const filteredData = responseData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.item_code?.toLowerCase().includes(searchLower) ||
      item.item_name?.toLowerCase().includes(searchLower) ||
      item.category?.toLowerCase().includes(searchLower) ||
      // item.type?.toLowerCase().includes(searchLower) ||
      item.status?.toLowerCase().includes(searchLower) ||
      item.pure_percent?.toLowerCase().includes(searchLower)    
    );
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 2;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Products Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; color: #333; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .status-active { background-color: #d4edda; color: #155724; padding: 4px 8px; border-radius: 4px; }
          .status-inactive { background-color: #f8d7da; color: #721c24; padding: 4px 8px; border-radius: 4px; }
          .table-secondary { background-color: #e2e3e5; }
          .table-danger { background-color: #f8d7da; }
          .table-info { background-color: #cff4fc; }
          @media print {
            body { margin: 0; }
            table { font-size: 12px; }
          }
        </style>
      </head>
      <body>
        <h1>Products Report</h1>
        <p><strong>Total Records:</strong> ${filteredData.length}</p>
        <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              {/* <th>Type</th> */}
              <th>Purity (%)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData
        .map((item, index) => {
          const userId = localStorage.getItem("username");
          const latestNotification = item.notifications?.find(
            (notif) => notif.employe_id === userId
          );
          const status = latestNotification?.status || "";
          let rowClass = "";
          switch (status) {
            case "accepted":
              rowClass = "table-secondary";
              break;
            case "rejected":
              rowClass = "table-danger";
              break;
            case "requested":
              rowClass = "table-info";
              break;
          }
          return `
                    <tr class="${rowClass}">
                      <td>${index + 1}</td>
                      <td>${item.item_code || ""}</td>
                      <td>${item.item_name || ""}</td>
                      <td>${item.category || ""}</td>
                      {/* <td>${item.type || ""}</td> */}
                      <td>${item.pure_percent || ''}</td>
                      <td>
                        <span class="${item.status === "Active"
              ? "status-active"
              : "status-inactive"
            }">
                          ${item.status || ""}
                        </span>
                      </td>
                    </tr>
                  `;
        })
        .join("")}
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);

    toast.success("Print dialog opened!", 2000, "topRight");
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.setTextColor(40);
      doc.text("Products Report", 105, 20, { align: "center" });
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Total Records: ${filteredData.length}`, 14, 35);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 42);

      const tableColumn = [
        "S.No",
        "Item Code",
        "Item Name",
        "Category",
        // "Type",
        "Purity (%)",
        "Status",
      ];
      const tableRows = filteredData.map((item, index) => [
        index + 1,
        item.item_code || "",
        item.item_name || "",
        item.category || "",
        // item.type || "",
        item.pure_percent || "",
        item.status || "",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 50,
        theme: "striped",
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: "bold",
        },
        bodyStyles: {
          textColor: 50,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { top: 50, left: 14, right: 14 },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
      });

      doc.save(`products-report-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch (error) {
      toast.error("Failed to export PDF", 3000, "topRight");
    }
  };

  const copyToClipboard = () => {
    try {
      const headers =
        "S.No\tItem Code\tItem Name\tCategory\tPurity (%)\tStatus\n"; // Type column removed
      const rows = filteredData
        .map(
          (item, index) =>
            `${index + 1}\t${item.item_code || ""}\t${item.item_name || ""}\t${item.category || ""
            }\t${item.pure_percent || ""}\t${item.status || "" /* \t${item.type || ""} - type removed */
            }`
        )
        .join("\n");

      const clipboardData = headers + rows;

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(clipboardData).then(() => {
          toast.success("Data copied to clipboard!", 3000, "topRight");
        });
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = clipboardData;
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Data copied to clipboard!", 3000, "topRight");
      }
    } catch (error) {
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  const exportCSV = () => {
    try {
      const headers = [
        "S.No",
        "Item Code",
        "Item Name",
        "Category",
        // "Type",
        "Purity (%)",
        "Status",
      ];
      const csvData = [
        headers,
        ...filteredData.map((item, index) => [
          index + 1,
          item.item_code || "",
          item.item_name || "",
          item.category || "",
          // item.type || "",
          item.pure_percent || "",
          item.status || "",
        ]),
      ];

      const csvContent = csvData
        .map((row) =>
          row
            .map((field) => {
              if (
                typeof field === "string" &&
                (field.includes(",") ||
                  field.includes('"') ||
                  field.includes("\n"))
              ) {
                return `"${field.replace(/"/g, '""')}"`;
              }
              return field;
            })
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `products-report-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV exported successfully!", 3000, "topRight");
    } catch (error) {
      toast.error("Failed to export CSV", 3000, "topRight");
    }
  };
  const handleUpdate = (id, item) => {
    const role = localStorage.getItem("role");

    // if (role === "Employee") {
    if (role === "Employee" || role === "Manager") {
      if (checkNotificationStatus(id, "Update")) {
        // Direct edit allowed
        populateUpdateData(item);
      } else {
        openNotificationModal(id, "Update");
        return;
      }
    } else {
      populateUpdateData(item);
    }
  };

  // ===== FIXED POPULATE UPDATE DATA =====
  const populateUpdateData = (item) => {
    console.log("Populating update form with:", item);
    console.log("Item pure_percent value:", item.pure_percent);
    console.log("Item pure_percent type:", typeof item.pure_percent);

    // Ensure pure_percent is always a string or empty string
    const purePercentValue = item.pure_percent !== null && item.pure_percent !== undefined
      ? String(item.pure_percent)
      : "";

    console.log("Setting pure_percent as:", purePercentValue);

    setUpdateData({
      id: item.id,
      item_code: item.item_code || "",
      item_name: item.item_name || "",
      item_name_english: item.item_name_english || item.item_name || "",
      item_name_tamil: item.item_name_tamil || "",
      category: item.category || "",
      type: item.type || "",
      status: item.status || "",
      pure_percent: purePercentValue, // This is the critical line!
      image: item.image || null,
      created_by: item.created_by || "",
      created_date: item.created_date || "",
      updated_by: "system",
      updated_date: "",
      remove_image: false,
    });

    setUpdateModal(true);
  };

  const exportExcel = () => {
    try {
      const headers = [
        "S.No",
        "Item Code",
        "Item Name",
        "Category",
        // "Type",
        "Purity (%)",
        "Status",
      ];
      const excelData = [
        headers,
        ...filteredData.map((item, index) => [
          index + 1,
          item.item_code || "",
          item.item_name || "",
          item.category || "",
          // item.type || "",
          item.pure_percent || "",
          item.status || "",
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
      ];
      const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "4F81BD" } },
          alignment: { horizontal: "center" },
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
      workbook.Props = {
        Title: "Products Report",
        Subject: "Product Data Export",
        Author: "Product Management System",
        CreatedDate: new Date(),
      };

      XLSX.writeFile(
        workbook,
        `products-report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Excel exported successfully!", 3000, "topRight");
    } catch (error) {
      toast.error("Failed to export Excel", 3000, "topRight");
    }
  };

  useEffect(() => {
    fetchData();
    fetchProductType();
    fetchNotifications();
    const interval = setInterval(() => {
      fetchData();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <h6 className="card-title m-2">Products</h6>
            <button
              className="btn btn-primary btn-sm rounded-3 ms-2"
              onClick={fetchDataGenerate}
            >
              Add <span className="ms-2">+</span>
            </button>
          </div>
          {/* Type button removed - type field no longer used */}
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex">
                <label className="form-label text-dark mt-2 mx-4">Show</label>
                <select
                  className="form-control mx-2"
                  onChange={handleItemsPerPageChange}
                  value={showAllItems ? "All" : itemsPerPage}
                >
                  <option value="All">All</option>
                  {[5, 10, 20, 100, 200, 500, 1000].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={handlePrint}
                >
                  Print
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportPDF}
                >
                  PDF
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportCSV}
                >
                  CSV
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportExcel}
                >
                  Excel
                </button>
              </div>

              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="card tableCard p-2">
              <div className="table-container">
                <table className="table table-layout-fixed w-100">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "50px" }}>S.No</th>
                      <th>Item Code</th>
                      <th style={{ width: "350px" }}> Item Name</th>
                      <th>Category</th>
                      {/* <th>Type</th> */}
                      <th>Purity (%)</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, index) => {
                      const userId = localStorage.getItem("username");
                      const latestNotification = item.notifications?.find(
                        (notif) =>
                          notif.table_id === item.id.toString() &&
                          notif.viewed === "false"
                      );
                      const status = latestNotification?.status || "";
                      let rowClass = "";
                      switch (status) {
                        case "accepted":
                          rowClass = "table-secondary";
                          break;
                        case "rejected":
                          rowClass = "table-danger";
                          break;
                        case "requested":
                          rowClass = "table-info";
                          break;
                      }
                      return (
                        <tr key={item.id || index} className={rowClass}>
                          <td>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>{item.item_code}</td>
                          <td>
  {item.item_name}
  {item.item_name_tamil && (
    <span className="text-muted"> / {item.item_name_tamil}</span>
  )}
</td>
                          <td>{item.category}</td>
                          {/* <td>{item.type}</td> */}
                          <td>{item.pure_percent ? `${item.pure_percent}%` : "-"}</td>
                          <td>
                            <span
                              className={`badge bg-${item.status === "Active" ? "success" : "danger"
                                } text-white`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() => handleUpdate(item.id, item)}
                            >
                              <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() =>
                                handleOpenDeleteModal(item.id, item.item_name)
                              }
                            >
                              <i className="fa-solid fa-trash text-danger fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <p className="text-muted">
                    Showing{" "}
                    {showAllItems ? 1 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                    {showAllItems
                      ? totalItems
                      : Math.min(currentPage * itemsPerPage, totalItems)}{" "}
                    of {totalItems} entries
                  </p>
                </div>
                <div className="col-lg-6">
                  <nav aria-label="Page navigation">
                    <ul className="pagination justify-content-end">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                        >
                          <span aria-hidden="true">««</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          <span aria-hidden="true">«</span>
                        </button>
                      </li>
                      {renderPageNumbers()}
                      <li
                        className={`page-item ${currentPage === totalPages || totalPages === 0
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          <span aria-hidden="true">»</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${currentPage === totalPages || totalPages === 0
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                        >
                          <span aria-hidden="true">»»</span>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Product Modal */}
      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{ display: showModal ? "block" : "none" }}
        tabIndex="-1"
        aria-labelledby="addProductModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="addProductModalLabel"
              >
                {actionType ? `${actionType} Request` : "Add Product"}
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={actionType ? closeNotificationModal : closeModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {actionType ? (
                <form onSubmit={handleNotificationSubmit}>
                  <div className="mb-3">
                    <p>
                      You are requesting to {actionType.toLowerCase()} this
                      product. An admin will review your request.
                    </p>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm px-4"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="item_code" className="form-label">
                          Item Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="item_code"
                          value={generateNumber}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="category" className="form-label">
                          Category
                        </label>
                        <select
                          className={`form-select ${formErrors.category ? "is-invalid" : ""
                            }`}
                          id="category"
                          value={addData.category}
                          onChange={(e) =>
                            setAddData({ ...addData, category: e.target.value })
                          }
                        >
                          <option value="" disabled>
                            Select Category
                          </option>
                          <option value="Gold">Gold</option>
                          <option value="Silver">Silver</option>
                          <option value="Gold Coin">Gold Coin</option>
                          <option value="Silver Coin">Silver Coin</option>
                        </select>
                        {formErrors.category && (
                          <div className="invalid-feedback">
                            {formErrors.category}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
  <div className="mb-3">
    <label htmlFor="item_name_english" className="form-label">
      Item Name <span className="text-danger">*</span>
    </label>
    <div className="input-group">
      <input
        type="text"
        className={`form-control ${formErrors.item_name ? "is-invalid" : ""}`}
        id="item_name_english"
        placeholder="Enter item name in English (Tamil will auto-translate)"
        value={addData.item_name_english}
        onChange={(e) =>
          setAddData({
            ...addData,
            item_name_english: e.target.value,
            item_name: e.target.value,
            // Clear Tamil name when English changes to trigger new translation
            item_name_tamil: "",
          })
        }
        onBlur={async (e) => {
          const val = e.target.value.trim();
          if (val && !addData.item_name_tamil) {
            const tamil = await translateToTamil(val);
            if (tamil) {
              setAddData(prev => ({ ...prev, item_name_tamil: tamil }));
              toast.info(`Tamil translation: ${tamil}`, 2000, "topRight");
            }
          }
        }}
      />
      <button
        type="button"
        className="btn btn-outline-secondary"
        title="Translate to Tamil"
        disabled={isTranslating || !addData.item_name_english}
        style={{height:"35px"}}
        onClick={async () => {
          const tamil = await translateToTamil(addData.item_name_english);
          if (tamil) {
            setAddData(prev => ({ ...prev, item_name_tamil: tamil }));
            toast.success("Translated successfully!", 1500, "topRight");
          } else {
            toast.error("Translation failed. Please try again.", 2000, "topRight");
          }
        }}
      >
        {isTranslating ? (
          <span className="spinner-border spinner-border-sm" />
        ) : (
          "🌐 Translate to Tamil"
        )}
      </button>
    </div>
    {addData.item_name_tamil && (
      <small className="text-muted mt-1 d-block">
        <strong>Tamil:</strong> {addData.item_name_tamil}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-2 text-muted"
          onClick={() => setAddData(prev => ({ ...prev, item_name_tamil: "" }))}
          title="Clear Tamil name (will auto-translate again)"
        >
          ✕ clear
        </button>
      </small>
    )}
    {formErrors.item_name && (
      <div className="invalid-feedback">{formErrors.item_name}</div>
    )}
  </div>
</div>
                  </div>

                  <div className="row">
                    
                  </div>

                  <div className="row">
                    <div className="col-lg-6 col-md-6">
                      <div className="mb-3">
                        <label htmlFor="pure_percent" className="form-label">
                          Purity (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          className={`form-control ${formErrors.pure_percent ? "is-invalid" : ""}`}
                          id="pure_percent"
                          placeholder="e.g. 99.9"
                          value={addData.pure_percent}
                          onChange={(e) =>
                            setAddData({ ...addData, pure_percent: e.target.value })
                          }
                        />
                        {formErrors.pure_percent && (
                          <div className="invalid-feedback">{formErrors.pure_percent}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">
                          Status
                        </label>
                        <select
                          className={`form-select ${formErrors.status ? "is-invalid" : ""
                            }`}
                          id="status"
                          value={addData.status}
                          onChange={(e) =>
                            setAddData({ ...addData, status: e.target.value })
                          }
                        >
                          <option value="" disabled>
                            Select Status
                          </option>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        {formErrors.status && (
                          <div className="invalid-feedback">
                            {formErrors.status}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="mb-3">
                      <label htmlFor="image" className="form-label">
                        Image Upload
                      </label>
                      <input
                        type="file"
                        className={`form-control ${formErrors.image ? "is-invalid" : ""
                          }`}
                        id="image"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                      {formErrors.image && (
                        <div className="invalid-feedback">
                          {formErrors.image}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm px-4"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Update Product Modal */}
      <div
        className={`modal fade ${updateModal ? "show" : ""}`}
        style={{ display: updateModal ? "block" : "none" }}
        tabIndex="-1"
        aria-labelledby="updateProductModalLabel"
        aria-hidden={!updateModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="updateProductModalLabel"
              >
                Update Product
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeUpdateModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="update_item_code" className="form-label">
                        Item Code
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="update_item_code"
                        value={updateData.item_code}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    {/* Type dropdown commented out */}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label htmlFor="update_item_name_english" className="form-label">
                        Item Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${formErrors.item_name ? "is-invalid" : ""}`}
                          id="update_item_name_english"
                          placeholder="Enter item name (Tamil will auto-fill)"
                          value={updateData.item_name_english || updateData.item_name}
                          onChange={(e) =>
                            setUpdateData({
                              ...updateData,
                              item_name_english: e.target.value,
                              item_name: e.target.value,
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val) {
                              const tamil = await translateToTamil(val);
                              if (tamil) setUpdateData(prev => ({ ...prev, item_name_tamil: tamil }));
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title="Translate to Tamil"
                          disabled={isTranslating || !(updateData.item_name_english || updateData.item_name)}
                          onClick={async () => {
                            const tamil = await translateToTamil(updateData.item_name_english || updateData.item_name);
                            if (tamil) setUpdateData(prev => ({ ...prev, item_name_tamil: tamil }));
                          }}
                        >
                          {isTranslating ? <span className="spinner-border spinner-border-sm" /> : "🌐 தமிழ்"}
                        </button>
                        {formErrors.item_name && (
                          <div className="invalid-feedback">
                            {formErrors.item_name}
                          </div>
                        )}
                      </div>
                      {updateData.item_name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <span className="me-1">🔤 Tamil:</span>
                          <strong>{updateData.item_name_tamil}</strong>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() => setUpdateData(prev => ({ ...prev, item_name_tamil: "" }))}
                          >✕ clear</button>
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="update_category" className="form-label">
                        Category
                      </label>
                      <select
                        className={`form-select ${formErrors.category ? "is-invalid" : ""
                          }`}
                        id="update_category"
                        value={updateData.category}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            category: e.target.value,
                          })
                        }
                      >
                        <option value="" disabled>
                          Select Category
                        </option>
                        <option value="Gold">Gold</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold Coin">Gold Coin</option>
                        <option value="Silver Coin">Silver Coin</option>
                      </select>
                      {formErrors.category && (
                        <div className="invalid-feedback">
                          {formErrors.category}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="update_pure_percent" className="form-label">
                          Purity (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          className={`form-control ${formErrors.pure_percent ? "is-invalid" : ""}`}
                          id="update_pure_percent"
                          placeholder="e.g. 91.6"
                          value={updateData.pure_percent}
                          onChange={(e) => {
                            console.log("pure_percent input changed to:", e.target.value);
                            setUpdateData({ ...updateData, pure_percent: e.target.value });
                          }}
                        />
                        {formErrors.pure_percent && (
                          <div className="invalid-feedback">{formErrors.pure_percent}</div>
                        )}
                      </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="update_status" className="form-label">
                        Status
                      </label>
                      <select
                        className={`form-select ${formErrors.status ? "is-invalid" : ""
                          }`}
                        id="update_status"
                        value={updateData.status}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="" disabled>
                          Select Status
                        </option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {formErrors.status && (
                        <div className="invalid-feedback">
                          {formErrors.status}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </div>

                <div className="row">
                  <div className="mb-3">
                    <label htmlFor="update_image" className="form-label">
                      Image Upload
                    </label>
                    <div className="d-flex align-items-center">
                      <input
                        type="file"
                        className="form-control"
                        id="update_image"
                        onChange={handleUpdateImageChange}
                        accept="image/*"
                      />
                      {(updateData.image || updateData.remove_image) && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm ms-2"
                          onClick={handleRemoveUpdateImage}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {updateData.image &&
                      typeof updateData.image === "string" && (
                        <small className="text-muted">
                          Current image: {updateData.image.split("/").pop()}
                        </small>
                      )}
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button type="submit" className="btn btn-primary btn-sm px-4">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div
        className={`modal fade ${deleteShowModals ? "show" : ""}`}
        style={{
          display: deleteShowModals ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="deleteConfirmModalLabel"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-confirm">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="d-flex justify-content-center mb-3">
                <div
                  className="icon-box d-flex justify-content-center align-items-center bg-light rounded-circle"
                  style={{ width: "80px", height: "80px" }}
                >
                  <i className="bi bi-exclamation-triangle text-danger fs-2"></i>
                </div>
              </div>
              <h4 className="modal-title mb-3 text-center">Are you sure?</h4>
              <h6 className="mb-4 text-center">
                Do you really want to delete this
                <span className="text-primary"> ({deleteName})</span>?
              </h6>
              <div className="d-flex btn-group">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm flex-grow-1"
                  onClick={handleCloseDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm flex-grow-1 mx-2"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(showModal || updateModal || deleteShowModals) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default Product;